import React, { useEffect, useMemo, useState } from "react";
import { TextField, CircularProgress, Box, Typography, Tooltip, IconButton } from "@material-ui/core";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { dbGet } from "utils/DBFetchers";

export default function PharmacySelector({
  fieldClassName,
  value,                 // string: RUT seleccionado (p.ej. "123456789")
  onChange,              // (rut: string | "") => void
  onOptionsLoaded,       // opcional: callback cuando se cargan las opciones
  program,               // opcional: filtra por programa
  only_with_wallet,      // opcional: true/false
  group_by_rut,        // opcional: true/false
  distributor_id,        // opcional: ID del distribuidor
  label = "Seleccione RUT",
  placeholder = "Escribí para buscar…",
  disabled = false,
  multiple = false,
  include_all = false,
  isWallet = false,
  date_to = null,
}) {
  const [options, setOptions] = useState([]);  // [{ id, rut, primaryName, allNames, namesCount, rutClean }]
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState("");

  // Helpers
  const digitsOnly = (s) => (s || "").toString().replace(/\D/g, "");
  const normalize = (s) =>
    (s || "")
      .toString()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase();

  const ALL_VALUE = "";

  const toOption = (item) => {
    const rut = String(item.rut || item.RUT || item.rut_number || "");
    const primary = item.first_pharmacy_name_with_rut || item.primary_name || item.name || item.nombre || "";
    const all = Array.isArray(item.all_pharmacy_names_with_rut) && item.all_pharmacy_names_with_rut.length
      ? item.all_pharmacy_names_with_rut.filter(Boolean)
      : Array.isArray(item.all_pharmacy_names) && item.all_pharmacy_names.length
        ? item.all_pharmacy_names.filter(Boolean)
        : (primary ? [primary] : []);
    const count = Number.isFinite(item.names_count) ? item.names_count : all.length;
    return {
      id: item.id ?? rut,
      rut,
      rutClean: digitsOnly(rut),
      primaryName: primary,
      allNames: all,
      namesCount: count,
    };
  };

  useEffect(() => {
    const fetch = async () => {
      if (disabled) {
        setOptions([]);
        return;
      }
      setLoading(true);
      try {
        let path = isWallet ? "wallet/internal/pharmacies?pagination=false&exclude_test_pharmacies=true&" : "pharmacies?";
        if (distributor_id) path += `distributor_id=${distributor_id}&`;
        if (date_to) {
          const formattedDate = date_to instanceof Date ? date_to.toISOString().split("T")[0] : date_to;
          path += `to_date=${formattedDate}&`;
        }
        const data = await dbGet(path);
        const pharmaciesList = Array.isArray(data) ? data : (data?.pharmacies || []);
        let mapped = pharmaciesList.map(toOption).filter(o => o.rutClean);
        if (include_all && mapped.length > 0) {
          mapped.unshift({
            id: "all_pharmacies_id",
            rut: ALL_VALUE,
            rutClean: "",
            primaryName: "Todas",
            allNames: [],
            namesCount: 0,
            isAll: true,
          });
        }
        setOptions(mapped);
        // Notificar las opciones cargadas si hay callback
        if (onOptionsLoaded) {
          onOptionsLoaded(mapped);
        }
      } catch (e) {
        setOptions([]);
      } finally {
        setLoading(false);
      }
    };
    fetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [distributor_id, disabled, isWallet, date_to]);

  // Mantener visible el RUT seleccionado en el input cuando cambia externamente
  useEffect(() => {
    if (!value) return;
    const found = options.find(o => o.rut === value);
    if (found && inputValue === "") setInputValue(found.rut);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, options]);

  const selectedOption = useMemo(() => {
    if (multiple) {
      return options.filter((o) => (Array.isArray(value) ? value.includes(o.rut) : false));
    }
    return options.find((o) => o.rut === value) || null;
  }, [options, value, multiple]);

  // Etiqueta visual: "<RUT>    <primary> (+N)" (omite (+N) si N-1 === 0)
  const buildLabel = (o) => {
    if (!o) return "";
    if (o.isAll) return o.primaryName;
    const extra = Math.max(0, (o.namesCount || 0) - 1);
    return extra > 0
      ? `${o.rut}    ${o.primaryName} (+${extra})`
      : `${o.rut}    ${o.primaryName}`;
  };

  // Filtro/Ranking: rut exacto/prefijo/substr > primary > any name
  const filterOptions = (opts, { inputValue }) => {
    const q = normalize(inputValue.trim());
    const qDigits = digitsOnly(inputValue);
    if (!q) return opts;

    const score = (o) => {
      const rutN = normalize(o.rut);
      const primaryN = normalize(o.primaryName);
      const namesN = o.allNames.map(normalize);

      // Priorizar RUT (también versión "sólo dígitos")
      if (o.rut === inputValue) return 100;
      if (o.rut.startsWith(inputValue)) return 95;
      if (o.rut.includes(inputValue)) return 90;

      // Si el usuario tipea sólo números, matchear por dígitos del RUT
      if (qDigits && o.rutClean.includes(qDigits)) return 85;

      if (rutN === q) return 80;
      if (rutN.startsWith(q)) return 75;
      if (rutN.includes(q)) return 70;

      if (primaryN.includes(q)) return 65;
      if (namesN.some(n => n.includes(q))) return 60;

      return 0;
    };

    return opts
      .map(o => ({ o, s: score(o) }))
      .filter(x => x.s > 0)
      .sort((a, b) => b.s - a.s)
      .map(x => x.o);
  };

  return (
    <Autocomplete
      className={fieldClassName}
      options={options}
      value={selectedOption}
      multiple={multiple}
      limitTags={2}
      disableCloseOnSelect={multiple}
      onChange={(_, newVal) => {
        if (multiple) {
          if (Array.isArray(newVal)) {
            const hasAll = newVal.some((o) => o.isAll);
            if (hasAll) {
              onChange([ALL_VALUE]);
            } else {
              onChange(newVal.map((o) => o.rut));
            }
          } else {
            onChange([]);
          }
        } else {
          onChange(newVal ? newVal.rut : "");
        }
      }}
      disabled={disabled}
      getOptionLabel={(o) => buildLabel(o)}
      renderOption={(option) => {
        const extra = Math.max(0, (option.namesCount || 0) - 1);
        const showBadge = extra > 0;
        const names = option.allNames || [];

        return (
          <Box display="flex" alignItems="center" width="100%">
            <Typography style={{ minWidth: 120, fontVariantNumeric: "tabular-nums" }}>
              {option.rut}
            </Typography>
            <Typography
              style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginLeft: 8 }}
              title={option.primaryName}
            >
              {option.primaryName}
            </Typography>
            {showBadge && (
              <Tooltip
                title={
                  <Box style={{ maxWidth: 360 }}>
                    {names.slice(0, 10).map((n, i) => (
                      <div key={`${option.rut}-${i}`}>{n}</div>
                    ))}
                    {names.length > 10 && <div>+{names.length - 10} más…</div>}
                  </Box>
                }
                enterDelay={200}
              >
                <IconButton
                  size="small"
                  aria-label={`Ver farmacias del RUT ${option.rut}`}
                  onMouseDown={(e) => e.preventDefault()} // evita perder selección
                >
                  <InfoOutlinedIcon fontSize="small" />
                  <span style={{ marginLeft: 4, fontSize: 12, opacity: 0.8 }}>+{extra}</span>
                </IconButton>
              </Tooltip>
            )}
          </Box>
        );
      }}
      inputValue={inputValue}
      onInputChange={(_, val) => setInputValue(val)}
      filterOptions={filterOptions}
      noOptionsText={inputValue ? "Sin resultados" : "Escribí para buscar…"}
      loading={loading}
      loadingText="Cargando…"
      autoHighlight
      openOnFocus
      clearOnBlur={false}
      selectOnFocus
      handleHomeEndKeys
      disableClearable={false}
      getOptionSelected={(o, v) => o.rut === v.rut}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="outlined"
          label={label}
          placeholder={placeholder}
          disabled={disabled}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={18} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
